import { useState, useEffect, useCallback } from "react";
import Jobs from "../../components/career/jobs";
import Search from "../../components/career/search";
import AnnouncementPanel from "../../components/career/announcement";
import tokenService from "../../services/token-service";
import axios from "axios";
import apiUrls from "../../config/api-urls";
import { ChevronLeft, ChevronRight } from "lucide-react";

const LIMIT = 10;

const JobListing = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applications, setApplications] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [searchCriteria, setSearchCriteria] = useState({ title: "", location: "" });

  const token = tokenService.getToken();

  // Fetch all job locations once on mount (large limit to get every job)
  const fetchAllLocations = async () => {
    try {
      const response = await axios.get(`${apiUrls.jobs.job}?limit=200&page=1`);
      const allJobs = response.data?.data?.jobs || [];
      const unique = [...new Set(allJobs.map((j) => j.location).filter(Boolean))].sort();
      setAllLocations(unique);
    } catch {
      setAllLocations([]);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(apiUrls.appstatus.status, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const apps = response.data?.applications || [];
      setApplications(Array.isArray(apps) ? apps : []);
    } catch {
      setApplications([]);
    }
  };

  const fetchJobs = useCallback(async (criteria = {}, page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        limit: LIMIT,
        ...(criteria.title ? { title: criteria.title } : {}),
        ...(criteria.location ? { location: criteria.location } : {}),
      }).toString();
      const response = await axios.get(`${apiUrls.jobs.job}?${params}`);
      const { jobs: jobData = [], pagination: paginationData = {} } =
        response.data?.data || {};
      setJobs(jobData);
      setPagination({
        currentPage: paginationData.currentPage || page,
        totalPages: paginationData.totalPages || 1,
        totalCount: paginationData.totalCount || 0,
        hasNextPage: paginationData.hasNextPage || false,
        hasPreviousPage: paginationData.hasPreviousPage || false,
      });
      setError(null);
    } catch {
      setError("Failed to load job listings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs({}, 1);
    fetchApplications();
    fetchAllLocations();
  }, []);

  const getJobStatus = (jobId) => {
    const app = applications.find((a) => a.jobId?._id === jobId);
    return app ? app.status : null;
  };

  const handleSearch = (criteria) => {
    setSearchCriteria(criteria);
    fetchJobs(criteria, 1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchJobs(searchCriteria, newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <AnnouncementPanel />
      <Search onSearch={handleSearch} loading={loading} allLocations={allLocations} />
      <Jobs jobs={jobs} loading={loading} error={error} getJobStatus={getJobStatus} />

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="container mx-auto px-4 pb-8 flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing{" "}
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {(pagination.currentPage - 1) * LIMIT + 1}–
              {Math.min(pagination.currentPage * LIMIT, pagination.totalCount)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {pagination.totalCount}
            </span>{" "}
            jobs
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPreviousPage || loading}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>

            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                disabled={loading}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 ${
                  page === pagination.currentPage
                    ? "bg-[#ff9906] text-white"
                    : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage || loading}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default JobListing;
